import axios from "axios";
import React, { useMemo, useState, useCallback, Suspense, lazy } from "react";
import {
  Domain_Data,
  GET_REFDOMAINLIMIT,
  POST_REFDOMAINLIMIT,
} from "../../../services/constants";
import Paper from "@mui/material/Paper";
import TableCell from "@mui/material/TableCell";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { ReactComponent as UnsortIcon } from "../../Assets/rank-tracking-assets/unshort.svg";
import { checkIfAdmin } from "../../../helperJs/helper";
import { Button } from "@mui/material";
import exportIcon from "../../Assets/rank-tracking-assets/export-file.svg";
import { CSVLink } from "react-csv";
import { curday } from "../../share/upDater/constant";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Checkbox from "@mui/material/Checkbox";
import { ZustandStore } from "../../../store/zustand-store";
import Spinner from "../../share/components/spinner";
import VirtualizeTable from "../../share/tables/DynamicTable/VirtualizeTable";
import Tooltip from "../../share/components/tooltip";
import TableSettings from "../../share/tables/DynamicTable/table-settings";
import { useTranslation } from "react-i18next";
import SemButton from "../../../common/components/Button";
import { useTableBlur } from "../../share/custom-hook/useTableBlur";
import AddProjectStore from "../../../store/AddProjectStore";

const BacklinkReffDomainTableFilter = lazy(() =>
  import("../../share/components/table-filter/BacklinkReffDomainTableFilter")
);

const BacklinkRefDomain = ({ domain, alertFn }) => {
  const rowsPerPage = 100;
  const email = localStorage.getItem("admin_mail");
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [loader, setLoader] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedData, setSelectedData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [FilterData, setFilterData] = useState({
    isFilter: false,
    data: [],
  });
  sessionStorage.setItem("FREE_TRIAL_TABLE", "reffDomainTable");
  const maxAllowedPage = 1; // for trial users
  const shouldTableBlur = useTableBlur({
    currentPage: page,
    blurFromPage: maxAllowedPage,
  });
  const {
    customer_plan,
    referring_domain,
    backlinkRefDomainColumn,
    updateBacklinkRefDomainTableColumn,
  } = ZustandStore((state) => state);
  const showNotification = AddProjectStore((state) => state.showNotification);

  const domainListData = useMemo(() => {
    if (referring_domain?.length === 0)
      return {
        total_reff_domains: 0,
        data: [],
      };
    const domains = referring_domain?.[0]?.data?.[0]?.data
      ? referring_domain?.[0]?.data?.[0]?.data
      : referring_domain?.[0]?.data;
    const total_refdomains = referring_domain?.[0]?.data?.[0]?.total_refdomains
      ? referring_domain?.[0]?.data?.[0]?.total_refdomains
      : referring_domain?.[0]?.total_refdomains;

    return {
      total_reff_domains: total_refdomains || 0,
      data: domains || [],
    };
  }, [referring_domain]);

  const columnArr = [
    {
      label: t(
        "backlinks.backlink_analysis.referring_domain.table.header.dr.title"
      ),
      align: "left",
      dataKey: "rank",
      sort: true,
      tooltip: t(
        "backlinks.backlink_analysis.referring_domain.table.header.dr.tooltip"
      ),
      width: "60px",
      show: !backlinkRefDomainColumn.includes("DR"),
      ignore: true,
    },
    {
      label: t(
        "backlinks.backlink_analysis.referring_domain.table.header.domain.title"
      ),
      align: "left",
      dataKey: "domain",
      sort: false,
      tooltip: t(
        "backlinks.backlink_analysis.referring_domain.table.header.domain.tooltip"
      ),
      width: "250px",
      show: !backlinkRefDomainColumn.includes("Domain"),
      ignore: true,
    },
    {
      label: t(
        "backlinks.backlink_analysis.referring_domain.table.header.backlinks.title"
      ),
      align: "center",
      dataKey: "backlinks",
      sort: true,
      tooltip: t(
        "backlinks.backlink_analysis.referring_domain.table.header.backlinks.tooltip"
      ),
      width: "100px",
      show: !backlinkRefDomainColumn.includes("Backlinks"),
      ignore: true,
    },

    {
      label: t(
        "backlinks.backlink_analysis.referring_domain.table.header.ref_pages.title"
      ),
      tooltip: t(
        "backlinks.backlink_analysis.referring_domain.table.header.ref_pages.tooltip"
      ),
      align: "center",
      dataKey: "referring_pages",
      sort: true,
      width: "100px",
      show: !backlinkRefDomainColumn.includes("Ref Pages"),
      ignore: true,
    },
    {
      label: t(
        "backlinks.backlink_analysis.referring_domain.table.header.ss.title"
      ),
      tooltip: t(
        "backlinks.backlink_analysis.referring_domain.table.header.ss.tooltip"
      ),
      align: "center",
      dataKey: "backlinks_spam_score",
      sort: true,
      width: "60px",
      show: !backlinkRefDomainColumn.includes("SS"),
      ignore: false,
    },
    {
      label: t(
        "backlinks.backlink_analysis.referring_domain.table.header.ref_ips.title"
      ),
      tooltip: t(
        "backlinks.backlink_analysis.referring_domain.table.header.ref_ips.tooltip"
      ),
      align: "center",
      dataKey: "referring_ips",
      sort: true,
      width: "90px",
      show: !backlinkRefDomainColumn.includes("Ref IPs"),
      ignore: false,
    },
    {
      label: t(
        "backlinks.backlink_analysis.referring_domain.table.header.ref_subnets.title"
      ),
      tooltip: t(
        "backlinks.backlink_analysis.referring_domain.table.header.ref_subnets.tooltip"
      ),
      align: "center",
      dataKey: "referring_subnets",
      sort: true,
      width: "120px",
      show: !backlinkRefDomainColumn.includes("Ref Subnets"),
      ignore: false,
    },
    {
      label: t(
        "backlinks.backlink_analysis.referring_domain.table.header.ref_location.title"
      ),
      tooltip: t(
        "backlinks.backlink_analysis.referring_domain.table.header.ref_location.tooltip"
      ),
      align: "center",
      dataKey: "referring_locations",
      sort: true,
      width: "120px",
      show: !backlinkRefDomainColumn.includes("Ref Locations"),
      ignore: false,
    },

    {
      label: t(
        "backlinks.backlink_analysis.referring_domain.table.header.first_seen.title"
      ),
      tooltip: t(
        "backlinks.backlink_analysis.referring_domain.table.header.first_seen.tooltip"
      ),
      align: "center",
      dataKey: "first_seen",
      sort: true,
      width: "110px",
      show: !backlinkRefDomainColumn.includes("First Seen"),
      ignore: false,
    },
  ];

  const columns = useMemo(() => {
    if (backlinkRefDomainColumn?.length > 0) {
      return columnArr?.filter((column) => {
        return !backlinkRefDomainColumn?.includes(column.label);
      });
    } else {
      return columnArr;
    }
  }, [backlinkRefDomainColumn, t]);

  const visibleRows = useMemo(() => {
    return shouldTableBlur?.isTrialUser
      ? domainListData.data.slice(0, 101)
      : domainListData.data;
  }, [shouldTableBlur?.isTrialUser, domainListData]);

  const filteredDomainList = useMemo(() => {
    if (!searchQuery) return visibleRows;
    return visibleRows?.filter((item) =>
      item.domain.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [searchQuery, visibleRows]);

  const tableData = useMemo(() => {
    const data = filteredDomainList?.map((value, index) => ({
      id: index,
      rank:
        Math.round(value?.domain_authority / 10) < 5
          ? 0
          : Math.round(value?.rank / 10) || 0,
      domain: value.domain,
      backlinks: value.backlinks,
      referring_pages: value.referring_pages,
      backlinks_spam_score: value.backlink_spam_score,
      referring_ips: value.referring_ips,
      referring_subnets: value.referring_subnets,
      referring_locations: Object.values(
        value.referring_locations || {}
      ).reduce((a, b) => a + b, 0),
      first_seen: value.first_seen?.split(" ")[0],
    }));
    return data;
  }, [filteredDomainList, t]);

  const refDomainData = useMemo(() => {
    if (FilterData.isFilter) {
      return FilterData.data;
    } else {
      return tableData;
    }
  }, [tableData, FilterData, t]);

  const sortRows = (rows, config) => {
    if (rows?.length === 0) return [];
    if (!config.key) return rows; // Return original rows if no sorting key is provided
    return [...rows].sort((a, b) => {
      let aValue = a[config.key];
      let bValue = b[config.key];

      // Normalize values for comparison (e.g., case-insensitive for strings)
      const normalizedA =
        typeof aValue === "string" ? aValue.toLowerCase() : aValue;
      const normalizedB =
        typeof bValue === "string" ? bValue.toLowerCase() : bValue;

      // Ascending or descending sorting
      if (config.direction === "asc") {
        return normalizedA > normalizedB ? 1 : -1;
      } else {
        return normalizedA < normalizedB ? 1 : -1;
      }
    });
  };

  const sortedRows = useMemo(
    () => sortRows(refDomainData, sortConfig),
    [refDomainData, sortConfig]
  );

  const listData = useMemo(() => {
    if (sortedRows?.length === 0) return [];
    return sortedRows?.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [sortedRows, page, t]);

  const requestSort = useCallback(
    (key) => {
      let direction = "asc";
      if (sortConfig.key === key && sortConfig.direction === "asc") {
        direction = "desc";
      }
      setSortConfig({ key, direction });
    },
    [sortConfig]
  );

  const handleChangePage = (event, newPage) => {
    const isRestricted =
      shouldTableBlur.showWarning && newPage >= maxAllowedPage;
    if (isRestricted) {
      // Optional: show toast or popup
      showNotification({
        message:
          "🚫You are on free trial now, Please pay and unlock more pages",
        severity: "error",
      });
      return;
    }
    const maxPage = Math.max(
      0,
      Math.ceil(refDomainData?.length / rowsPerPage) - 1
    );
    if (newPage < 0) {
      setPage(0); // Reset to the first page if negative
    } else if (newPage > maxPage) {
      setPage(maxPage); // Reset to the last page if exceeding limits
    } else {
      setPage(newPage); // Otherwise, set the provided page
    }
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
  };

  const handleRefDomainData = (config, postCall) => {
    axios
      .get(Domain_Data(config.domain))
      .then((res) => {
        if (res.data?.length === 0 && postCall) {
          axios
            .post(
              `https://app.rankspro.io/api/user/refdomain/?domain=${config.domain}&include_subdomains=true`
            )
            .then((response) => {
              ZustandStore.setState({
                referring_domain: response.data,
              });
              setLoader(false);
            })
            .catch(() => {
              ZustandStore.setState({
                referring_domain: [],
              });
              setLoader(false);
            });
        } else {
          ZustandStore.setState({
            referring_domain: res.data,
          });
          setLoader(false);
        }
      })
      .catch((err) => {
        console.error("Error fetching data:", err);
        ZustandStore.setState({
          referring_domain: [],
        });
        setLoader(false);
      });
  };

  const handleRefDomLimit = (config) => {
    const searchLimit = customer_plan?.ref_domain || 0;
    axios
      .get(GET_REFDOMAINLIMIT(config))
      .then((response) => {
        if (
          response.data?.length >= searchLimit &&
          !checkIfAdmin(config.email)
        ) {
          handleRefDomainData(config, false);
          const alert = {
            type: "error",
            message:
              "Oops! Looks like your Referring Domain Limit Exhausted. Please Upgrade.",
            show: true,
          };
          alertFn(alert);
          return;
        }
        const filterArr = response.data?.filter((item) => {
          return item.domain === config.domain;
        });
        if (response?.data?.length === 0 || filterArr?.length === 0) {
          const Payload = {
            email: config.email,
            domain: config.domain,
            limit: 1,
          };
          axios
            .post(POST_REFDOMAINLIMIT(), Payload)
            .then((response) => {})
            .catch((error) => {
              console.error("ERR: REF_DOMAIN LIMIT POST FAILED", error);
            });
        }
        handleRefDomainData(config, true);
      })
      .catch((error) => {
        console.error("ERR: REF_DOMAIN LIMIT GET FAILED", error);
        setLoader(false);
      });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!domain) {
      alert(t("backlinks.please_enter_domain"));
      return;
    }
    const config = {
      email: email,
      domain,
    };
    setLoader(true);
    ZustandStore.setState({
      referring_domain: [],
    });
    handleRefDomLimit(config);
  };

  const isAllKeywordSelected = useMemo(() => {
    const selected = selectedData || [];
    const allKeywords = refDomainData || [];
    return selected.length === 0
      ? false
      : allKeywords.length === selected.length;
  }, [selectedData, refDomainData]);

  const handleSelectAllKeyword = () => {
    if (selectedData?.length === 0) {
      setSelectedData(refDomainData);
    } else {
      setSelectedData([]);
    }
  };

  const isCheckboxChecked = (index) => {
    if (selectedData?.length === 0) return false;
    return selectedData?.find((item) => item.id === index) ? true : false;
  };

  const handleProjectSelection = (obj) => {
    setSelectedData((prevSelectedId) => {
      if (prevSelectedId.find((item) => item.id === obj.id)) {
        const newId = prevSelectedId.filter((item) => item.id !== obj.id);
        return newId;
      } else {
        return [...prevSelectedId, obj];
      }
    });
  };

  const loadCSVData = () => {
    if (selectedData?.length === 0) {
      return refDomainData;
    } else {
      return selectedData;
    }
  };

  function fixedHeaderContent() {
    return (
      <TableRow>
        <TableCell
          padding="checkbox"
          key={"checkbox"}
          variant="head"
          align="left"
          sx={{
            backgroundColor: "#F4F5F9",
            maxWidth: "35px"
          }}
          onClick={handleSelectAllKeyword}
        >
          <Checkbox
            className="p-1"
            sx={{
              color: "#C4C7CF",
              "&.Mui-checked": {
                color: "#365486",
              },
            }}
            checked={isAllKeywordSelected}
          />
        </TableCell>

        {columns.map((column) => (
          <TableCell
            key={column.dataKey}
            align={column.align}
            variant="head"
            className="font-medium"
            sx={{
              fontSize: "0.9em",
              color: "#191B23",
              padding: "5px 8px",
              backgroundColor: "#F4F5F9",
              width: column?.width ? column.width : "auto",
            }}
          >
            <span
              className={column?.sort && column?.dataKey ? "point" : ""}
              onClick={() => {
                column?.sort && column?.dataKey && requestSort(column.dataKey);
              }}
            >
              {column.label}
              {column?.sort && column?.dataKey && (
                <span className="ms-2">
                  <UnsortIcon />
                </span>
              )}
              {column?.tooltip && <Tooltip title={column.tooltip} />}
            </span>
          </TableCell>
        ))}
      </TableRow>
    );
  }

  function rowContent(_index, row) {
    return (
      <React.Fragment>
        <TableCell
          padding="checkbox"
          onClick={() => handleProjectSelection(row)}
        >
          <Checkbox
            className="p-1"
            sx={{
              color: "#C4C7CF",
              "&.Mui-checked": {
                color: "#365486",
              },
            }}
            checked={isCheckboxChecked(row.id)}
          />
        </TableCell>
        {columns.map((column) => (
          <TableCell
            key={column.dataKey}
            align={column.align}
            className="font-medium"
            sx={{
              fontSize: "1em",
              color: "#191B23",
              padding: "5px",
            }}
          >
            {column.dataKey === "domain" ? (
              <div
                style={{
                  alignItems: "center",
                  display: "flex",
                  justifyContent: "flex-start",
                }}
              >
                <div
                  style={{
                    width: "max-content",
                    maxWidth: "200px",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    color: "#365486",
                    fontWeight: "400",
                  }}
                >
                  <a
                    href={`https://${row[column.dataKey]}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "none" }}
                  >
                    {row[column.dataKey]}
                  </a>
                </div>
                <OpenInNewIcon
                  sx={{
                    fontSize: "15px",
                    marginLeft: "4px",
                    color: "#365486",
                  }}
                />
              </div>
            ) : (
              row[column.dataKey]
            )}
          </TableCell>
        ))}
      </React.Fragment>
    );
  }

  const tableMessage = useMemo(() => {
    if (filteredDomainList?.length === 0)
      return `No results found for "${searchQuery}"`;
    return "No data available";
  }, [filteredDomainList, searchQuery, t]);

  return (
    <>
      {loader ? (
        <Spinner />
      ) : domainListData?.data?.length > 0 ? (
        <>
          <div className="align-items-center flexWrapDiv">
            <p className="mb-0 ttl-bklnk">
              {t(
                "backlinks.backlink_analysis.referring_domain.total_ref_domain"
              )}
              <span className="ms-1">
                {domainListData?.data?.length}
                {""}/{""}
                {domainListData?.total_reff_domains || 0}
              </span>
            </p>

            <div className="backlink-search-filed ms-auto">
              <i className="fa-solid fa-magnifying-glass icon"></i>
              <input
                className="input-field heading-small"
                type="text"
                placeholder={t(
                  "backlinks.backlink_analysis.buttons.search.placeholder"
                )}
                value={searchQuery || ""}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
            </div>

            <CSVLink
              filename={"SEO Tools:" + domain + curday() + ".csv"}
              data={loadCSVData()}
            >
              <SemButton
                title={t(
                  "rank_tracking.rank_tracker.table_filter.export_file.title"
                )}
                name={t("common_buttons.export")}
                disabled={refDomainData?.length === 0}
                color="#e95655"
                icon={<img src={exportIcon} alt="icon" />}
              />
            </CSVLink>
            <TableSettings
              {...{
                columns: columnArr,
                updateProjectTableCol: updateBacklinkRefDomainTableColumn,
              }}
            />
          </div>

          <Suspense fallback={null}>
            <BacklinkReffDomainTableFilter
              {...{
                headerArray: columns,
                tableData,
                setFilterData,
              }}
            />
          </Suspense>

          <Paper
            className={"w-100 mt-3 vir-table-border"}
            sx={{
              backgroundColor: "background.paper",
              boxShadow: "none",
              "& .MuiTableContainer-root": {
                boxShadow: "none",
                borderRadius: 0,
              },
            }}
          >
            <VirtualizeTable
              {...{
                rowContent,
                tableMessage,
                data: listData,
                loading: loader,
                fixedHeaderContent,
                columnLength: columns?.length + 1,
                isTableBlur: shouldTableBlur.showWarning,
              }}
            />

            <TablePagination
              sx={{
                borderTop: "1px solid rgba(224, 224, 224, 1)",
                cursor: "default",
                "& .MuiTablePagination-actions": {
                  width: 90,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                },
                "& .MuiTablePagination-displayedRows": {
                  padding: 0,
                  margin: 0,
                },
                "& .MuiTablePagination-selectLabel": {
                  padding: 0,
                  margin: 0,
                },
              }}
              rowsPerPageOptions={[100]}
              component="div"
              count={domainListData?.data?.length || 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={() => {}}
            />
          </Paper>
        </>
      ) : (
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            paddingBottom: "20px",
            fontSize: "17px",
          }}
        >
          <p>
            {" "}
            {t(
              "backlinks.backlink_analysis.referring_domain.check_ref_domain"
            )}{" "}
            <Button variant="contained">
              <span onClick={handleSubmit}>
                {t(
                  "backlinks.backlink_analysis.referring_domain.start_analyzing"
                )}
              </span>
            </Button>
          </p>
        </div>
      )}
    </>
  );
};

export default React.memo(BacklinkRefDomain);
